import React, { useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ArrowRight, MapPin, Users, Home } from "lucide-react";

interface CityData {
  name: string;
  nameDe: string;
  slug: string;
  lat: number;
  lon: number;
  delta: number;
  canton: string;
}

const CITIES: CityData[] = [
  { name: "Zurich", nameDe: "Zürich", slug: "zurich", lat: 47.3769, lon: 8.5417, delta: 0.3, canton: "ZH" },
  { name: "Basel", nameDe: "Basel", slug: "basel", lat: 47.5596, lon: 7.5886, delta: 0.25, canton: "BS" },
  { name: "Bern", nameDe: "Bern", slug: "bern", lat: 46.9480, lon: 7.4474, delta: 0.25, canton: "BE" },
  { name: "Lucerne", nameDe: "Luzern", slug: "luzern", lat: 47.0502, lon: 8.3093, delta: 0.2, canton: "LU" },
  { name: "St. Gallen", nameDe: "St. Gallen", slug: "st-gallen", lat: 47.4245, lon: 9.3767, delta: 0.2, canton: "SG" },
  { name: "Winterthur", nameDe: "Winterthur", slug: "winterthur", lat: 47.5001, lon: 8.7240, delta: 0.18, canton: "ZH" },
  { name: "Lausanne", nameDe: "Lausanne", slug: "lausanne", lat: 46.5197, lon: 6.6323, delta: 0.2, canton: "VD" },
  { name: "Geneva", nameDe: "Genf", slug: "genf", lat: 46.2044, lon: 6.1432, delta: 0.2, canton: "GE" },
  { name: "Biel/Bienne", nameDe: "Biel/Bienne", slug: "biel", lat: 47.1368, lon: 7.2467, delta: 0.18, canton: "BE" },
  { name: "Thun", nameDe: "Thun", slug: "thun", lat: 46.7580, lon: 7.6280, delta: 0.18, canton: "BE" },
  { name: "Aarau", nameDe: "Aarau", slug: "aarau", lat: 47.3925, lon: 8.0444, delta: 0.18, canton: "AG" },
  { name: "Fribourg", nameDe: "Freiburg", slug: "fribourg", lat: 46.8065, lon: 7.1620, delta: 0.18, canton: "FR" },
  { name: "Schaffhausen", nameDe: "Schaffhausen", slug: "schaffhausen", lat: 47.6960, lon: 8.6340, delta: 0.18, canton: "SH" },
  { name: "Chur", nameDe: "Chur", slug: "chur", lat: 46.8508, lon: 9.5320, delta: 0.18, canton: "GR" },
  { name: "Zug", nameDe: "Zug", slug: "zug", lat: 47.1724, lon: 8.5173, delta: 0.18, canton: "ZG" },
  { name: "Baden", nameDe: "Baden", slug: "baden", lat: 47.4734, lon: 8.3064, delta: 0.15, canton: "AG" },
  { name: "Rapperswil", nameDe: "Rapperswil", slug: "rapperswil", lat: 47.2267, lon: 8.8184, delta: 0.15, canton: "SG" },
  { name: "Olten", nameDe: "Olten", slug: "olten", lat: 47.3520, lon: 7.9077, delta: 0.15, canton: "SO" },
];

const FILTERS_STORAGE_KEY = "flateroo_browse_filters";

const CityLanding: React.FC = () => {
  const { city: citySlug } = useParams<{ city: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language?.startsWith("de");

  const cityData = useMemo(
    () => CITIES.find((c) => c.slug === citySlug),
    [citySlug],
  );

  const otherCities = useMemo(
    () => CITIES.filter((c) => c.slug !== citySlug),
    [citySlug],
  );

  const cityName = cityData
    ? isGerman
      ? cityData.nameDe
      : cityData.name
    : citySlug || "";

  const seoTitle = useMemo(() => {
    if (!cityData) return "";
    return isGerman
      ? `WG Zimmer ${cityName} – Freie WG Zimmer & Wohngemeinschaften | Flateroo`
      : `Shared Flats ${cityName} – Find WG Rooms & Flatmates | Flateroo`;
  }, [cityData, cityName, isGerman]);

  const seoDescription = useMemo(() => {
    if (!cityData) return "";
    return isGerman
      ? `Finde freie WG Zimmer in ${cityName}, ${cityData.canton}. Durchsuche verfügbare Wohngemeinschaften und bewirb dich direkt mit Video. Die moderne Alternative zu flatfox, Ron Orp & WG gesucht.`
      : `Find shared flats in ${cityName}, ${cityData.canton}. Browse available WG rooms and apply directly with video. The modern alternative to flatfox & WG gesucht.`;
  }, [cityData, cityName, isGerman]);

  const handleSearch = useCallback(() => {
    if (!cityData) return;

    const filters = {
      priceRange: { min: 0, max: 10000 },
      flatmateCount: 20,
      minRoomSize: 0,
      genderFilter: false,
      stayType: "both",
      appliedFilter: "Show all",
      selectedLocation: {
        name: cityData.nameDe,
        lat: cityData.lat,
        lon: cityData.lon,
        delta: cityData.delta,
      },
      searchInput: cityData.nameDe,
      mapRegion: {
        lat: cityData.lat,
        lon: cityData.lon,
        delta: cityData.delta,
      },
    };

    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    navigate("/");
  }, [cityData, navigate]);

  useEffect(() => {
    if (!cityData) return;

    document.title = seoTitle;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("name", "description", seoDescription);
    setMeta("property", "og:title", seoTitle);
    setMeta("property", "og:description", seoDescription);
    setMeta("property", "og:url", `https://flateroo.com/wg-zimmer/${cityData.slug}`);
    setMeta("property", "og:type", "website");

    let canonical = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://flateroo.com/wg-zimmer/${cityData.slug}`);

    let ldJson = document.querySelector("script[data-city-ld]") as HTMLScriptElement | null;
    if (!ldJson) {
      ldJson = document.createElement("script");
      ldJson.setAttribute("type", "application/ld+json");
      ldJson.setAttribute("data-city-ld", "true");
      document.head.appendChild(ldJson);
    }
    ldJson.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: seoTitle,
      description: seoDescription,
      url: `https://flateroo.com/wg-zimmer/${cityData.slug}`,
      isPartOf: {
        "@type": "WebApplication",
        name: "Flateroo",
        url: "https://flateroo.com",
      },
      about: {
        "@type": "City",
        name: cityData.nameDe,
        containedInPlace: { "@type": "Country", name: "Switzerland" },
      },
    });

    return () => {
      ldJson?.remove();
    };
  }, [cityData, seoTitle, seoDescription]);

  if (!cityData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-gnarly-semibold mb-4">
            {t("city_not_found") || "City not found"}
          </h1>
          <Link
            to="/"
            className="text-[#5b3df6] underline font-gnarly-semibold"
          >
            {t("browse_wgs") || "Browse WGs"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-white flex flex-col">
        {/* Hero */}
        <section className="relative flex flex-col items-center px-6 pt-32 sm:pt-40 pb-16 overflow-hidden">
          {/* Decorative grid */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(91,61,246,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(91,61,246,0.04) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          {/* Decorative accent dot */}
          <div
            className="absolute top-12 right-12 w-3 h-3 rounded-full hidden md:block"
            style={{ backgroundColor: "#E2FF49" }}
          />
          <div
            className="absolute bottom-24 left-16 w-2 h-2 rounded-full hidden md:block"
            style={{ backgroundColor: "#49FFC1" }}
          />

          <div className="relative max-w-2xl mx-auto text-center">
            {/* Canton badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-6 text-xs font-gnarly-semibold uppercase tracking-widest"
              style={{ backgroundColor: "#FAFFE2", color: "#5C6A00" }}
            >
              <MapPin size={12} />
              {cityData.canton}
            </div>

            <h1 className="font-gnarly-bold text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mb-5 min-h-[2.1em]"
              style={{ color: "rgba(2, 2, 7, 0.89)" }}
            >
              {isGerman ? "WG Zimmer in" : "Shared flats in"}{" "}
              <span style={{ color: "#5b3df6" }}>{cityName}</span>
            </h1>

            <p className="text-lg sm:text-xl leading-relaxed mb-10 max-w-lg mx-auto"
              style={{ color: "rgba(2, 2, 14, 0.58)" }}
            >
              {isGerman
                ? `Finde dein WG Zimmer in ${cityName}. Durchsuche verfügbare Wohngemeinschaften und bewirb dich direkt – mit Video oder Text.`
                : `Find your shared flat in ${cityName}. Browse available rooms and apply directly – with video or text.`}
            </p>

            {/* CTA */}
            <button
              onClick={handleSearch}
              className="group inline-flex items-center gap-3 px-8 py-4 min-h-[56px] rounded-full text-lg font-gnarly-semibold transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
              style={{
                backgroundColor: "#5b3df6",
                color: "rgba(255,255,255,0.9)",
                boxShadow: "0 4px 24px rgba(91,61,246,0.3)",
              }}
            >
              <Search size={20} />
              {isGerman
                ? `WGs in ${cityName} durchsuchen`
                : `Browse WGs in ${cityName}`}
              <ArrowRight
                size={18}
                className="transition-transform duration-200 group-hover:translate-x-1"
              />
            </button>
          </div>
        </section>

        {/* Features row */}
        <section className="px-6 pb-16">
          <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                icon: <Home size={20} />,
                titleKey: "landing_feature_rooms",
                titleFallbackEn: "Browse rooms",
                titleFallbackDe: "Zimmer durchsuchen",
                descKey: "landing_feature_rooms_desc",
                descFallbackEn: "Filter by price, size, location and more",
                descFallbackDe:
                  "Filtere nach Preis, Grösse, Lage und mehr",
              },
              {
                icon: <Users size={20} />,
                titleKey: "landing_feature_apply",
                titleFallbackEn: "Apply directly",
                titleFallbackDe: "Direkt bewerben",
                descKey: "landing_feature_apply_desc",
                descFallbackEn:
                  "Send text or video applications to stand out",
                descFallbackDe:
                  "Sende Text- oder Video-Bewerbungen",
              },
              {
                icon: <MapPin size={20} />,
                titleKey: "landing_feature_map",
                titleFallbackEn: "Map view",
                titleFallbackDe: "Kartenansicht",
                descKey: "landing_feature_map_desc",
                descFallbackEn: "See all available WGs on an interactive map",
                descFallbackDe:
                  "Sieh alle verfügbaren WGs auf einer interaktiven Karte",
              },
            ].map((feature) => (
              <div
                key={feature.titleKey}
                className="p-5 rounded-2xl flex flex-col items-center text-center"
                style={{ backgroundColor: "rgba(2, 2, 16, 0.03)" }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                  style={{
                    backgroundColor: "rgba(91, 61, 246, 0.1)",
                    color: "#5b3df6",
                  }}
                >
                  {feature.icon}
                </div>
                <h2
                  className="font-gnarly-semibold text-sm mb-1"
                  style={{ color: "rgba(2, 2, 7, 0.89)" }}
                >
                  {t(feature.titleKey) ||
                    (isGerman
                      ? feature.titleFallbackDe
                      : feature.titleFallbackEn)}
                </h2>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "rgba(2, 2, 14, 0.58)" }}
                >
                  {t(feature.descKey) ||
                    (isGerman
                      ? feature.descFallbackDe
                      : feature.descFallbackEn)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Other cities for internal linking */}
        <section className="px-6 pb-20">
          <div className="max-w-3xl mx-auto">
            <h2
              className="font-gnarly-semibold text-lg mb-5 text-center"
              style={{ color: "rgba(2, 2, 7, 0.89)" }}
            >
              {isGerman
                ? "WG Zimmer in anderen Städten"
                : "Shared flats in other cities"}
            </h2>
            <div className="flex flex-wrap justify-center gap-2">
              {otherCities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/wg-zimmer/${c.slug}`}
                  className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-150 hover:scale-[1.04] active:scale-[0.97]"
                  style={{
                    backgroundColor: "rgba(2, 2, 16, 0.05)",
                    color: "rgba(2, 2, 7, 0.89)",
                  }}
                >
                  {isGerman ? c.nameDe : c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
  );
};

export default CityLanding;
